import * as angular from "angular";
import { Campaign } from "../models/campaign.model";
import { Division } from "../models/division.model";
import { DivisionService } from "../services/division.service";

export class DivisionDataProvider {
    private $q: any;
    private service: DivisionService;

    public divisions = null;
    private allDivisionsPromise = null;
    private editingDivisionsPromise = null;

    constructor($q: any, DivisionService: DivisionService) {
        this.$q = $q;
        this.service = DivisionService;
    }

    clearAll(): void {
        this.divisions = null;
        this.allDivisionsPromise = null;
        this.editingDivisionsPromise = null;
    }

    createDivision(division: Division): Promise<Division> {
        const deferred = this.$q.defer();
        this.service.create(division).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    const division = new Division(json.data);
                    this.divisions.unshift(division);
                    deferred.resolve(division);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );

        this.editingDivisionsPromise = deferred.promise;
        return this.editingDivisionsPromise;
    }

    updateDivision(division: Division): Promise<any> {
        const deferred = this.$q.defer();
        this.service.update(division).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                const division = new Division(json.data);
                this.replaceDivision(division);
                deferred.resolve(division);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    spliceDivision(divisionDeleted: Division): void {
        angular.forEach(this.divisions, (division: Division, index: number) => {
            if (division.id && division.id == divisionDeleted.id) {
                const tempCampaigns = division.campaigns;
                this.divisions.splice(index, 1);
                tempCampaigns.forEach((campaign) => {
                    this.divisions[this.divisions.length - 1].campaigns.unshift(
                        campaign
                    );
                });
            }
        });
    }

    deleteDivision(divisionID: string): Promise<any> {
        const deferred = this.$q.defer();
        this.service
            .delete({ id: parseInt(divisionID) })
            .then((response: object) => {
                const json = response["data"];
                if (json.success) {
                    const division = new Division(json.data);
                    this.spliceDivision(division);
                    deferred.resolve(division);
                } else {
                    deferred.reject(json);
                }
            });
        return deferred.promise;
    }

    getDivisionCampaigns(): Promise<any> {
        const deferred = this.$q.defer();
        this.service.divisionCampaigns().then((response: object) => {
            const json = response["data"];
            if (json.success) {
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    getAllDivisions(needsStats: boolean = false): Promise<Division[]> {
        if (this.divisions !== null) {
            return this.$q.resolve(this.divisions);
        } else if (this.allDivisionsPromise) {
            //Has been called already and has not resolved yet.
            return this.allDivisionsPromise;
        } else {
            const deferred = this.$q.defer();
            this.service.all().then(
                (response: object) => {
                    const json = response["data"];
                    this.divisions = [];
                    for (let i = 0; i < json.data.length; i++) {
                        this.divisions.push(new Division(json.data[i]));
                    }
                    deferred.resolve(this.divisions);
                },
                (error: object) => {
                    this.divisions = [];
                    deferred.reject(error);
                }
            );
            this.allDivisionsPromise = deferred.promise;
            return this.allDivisionsPromise;
        }
    }

    replaceDivision(division: Division): void {
        for (let i = 0; i < this.divisions.length; i++) {
            if (this.divisions[i].id === division.id) {
                this.divisions[i] = division;
            }
        }
    }

    addToDefaultCampaigns(newCampaigns: Array<Campaign>): void {
        //add missing campaigns to default division
        let campaigns = this.divisions[this.divisions.length - 1].campaigns;
        campaigns.concat(newCampaigns); //default division is always last on list

        angular.forEach(newCampaigns, (newCampaign: Campaign) => {
            campaigns.push(newCampaign);
        });
    }

    spliceCampaign(campaign: Campaign, campaigns: Array<Campaign>): void {
        //Remove campaign from division campaigns
        if (campaigns !== null) {
            angular.forEach(
                campaigns,
                (divisionCampaign: Campaign, index: number) => {
                    if (divisionCampaign.campaignid == campaign.campaignid) {
                        campaigns.splice(index, 1);
                    }
                }
            );
        }
    }

    updateOrder(data: object): Promise<any> {
        const deferred = this.$q.defer();
        this.service.updateOrder(data).then(() => {
            deferred.resolve(data);
        });
        return deferred.promise;
    }

    updateCampaignsOrder(data: object): Promise<any> {
        const deferred = this.$q.defer();
        this.service.updateCampaignsOrder(data).then(() => {
            deferred.resolve(data);
        });
        return deferred.promise;
    }

    divisionByID(id: number): Division | boolean {
        for (let division of this.divisions) {
            if (division.divisionid === id) {
                return division;
            }
        }
        return false;
    }

    updateDivisions(divisions: Array<Division>): void {
        if (!this.divisions) {
            this.divisions = [];
        }

        for (let i = 0; i < divisions.length; i++) {
            const updatedDivision = divisions[i];
            let existingDiv = this.divisionByID(updatedDivision.divisionid);

            if (existingDiv) {
                angular.extend(existingDiv, updatedDivision);
            } else {
                this.divisions.unshift(new Division(updatedDivision));
            }
        }
    }

    filteredDivisions(
        divisions: Array<Division>,
        searchString: string
    ): Array<Division> {
        if (
            divisions == null ||
            !angular.isDefined(searchString) ||
            searchString === ""
        ) {
            return divisions;
        }

        return divisions.filter((division: Division) => {
            const divisionsWithCampaign =
                angular.isDefined(division.campaigns) &&
                division.campaigns != null
                    ? division.campaigns.filter((campaign) => {
                          return (
                              campaign.campaignname
                                  .toLowerCase()
                                  .indexOf(searchString.toLowerCase()) !== -1
                          );
                      })
                    : [];

            return (
                division.name
                    .toLowerCase()
                    .indexOf(searchString.toLowerCase()) !== -1 ||
                divisionsWithCampaign.length > 0
            );
        });
    }

    getDivisionUsers(
        divisionID: string,
        divisionSpecific: boolean = false
    ): Promise<any> {
        const deferred = this.$q.defer();
        if (divisionID) {
            this.service
                .getDivisionUsers({
                    id: parseInt(divisionID),
                    divisionSpecific: divisionSpecific,
                })
                .then(
                    (response: object) => {
                        const json = response["data"];
                        deferred.resolve(json.data);
                    },
                    (error: object) => deferred.reject(error)
                );
        } else {
            this.service.getDivisionUsers([]).then(
                (response: object) => {
                    const json = response["data"];
                    deferred.resolve(json.data);
                },
                (error: object) => deferred.reject(error)
            );
        }
        return deferred.promise;
    }

    processSocketCreate(socketData: any): void {
        const socketDivisions = socketData.divisions;

        if (this.editingDivisionsPromise) {
            this.editingDivisionsPromise.then(() => {
                this.updateDivisions(socketDivisions);
            });
        } else {
            this.updateDivisions(socketDivisions);
        }
    }
}

angular
    .module("vcDataProvider")
    .service("DivisionDataProvider", DivisionDataProvider);
